import { ApiHandlerResult } from '..'
import { Fetcher } from '@/api/fetcher'
import type { createArticleImageAction } from '@/utils/actions/create-article-image.action'
import type { deleteMediaImageAction } from '@/utils/actions/delete-media-image.action'
import type {
  generateImageMetadataAction,
  generateImageMetadataFromUrlAction,
} from '@/utils/actions/generate-image-metadata.action'
import type { getMediaImagesAction } from '@/utils/actions/get-media-images.action'
import type { presignMediaUploadAction } from '@/utils/actions/presign-media-upload.action'
import type { searchPhotographerSuppliersAction } from '@/utils/actions/search-photographer-suppliers.action'
import type { updateMediaImageAction } from '@/utils/actions/update-media-image.action'
import { BaseApi } from '../base'

// Inputs/results inferred from the Server Actions these routes wrap (the
// imports above are type-only, so no `server-only` code reaches the client).
type PresignInput = Parameters<typeof presignMediaUploadAction>[0]
type PresignResult = Awaited<ReturnType<typeof presignMediaUploadAction>>
type GenerateInput = Parameters<typeof generateImageMetadataAction>[0]
type GenerateResult = Awaited<ReturnType<typeof generateImageMetadataAction>>
type ArticleImageInput = Parameters<typeof createArticleImageAction>[0]
type ArticleImageResult = Awaited<ReturnType<typeof createArticleImageAction>>
type SearchSuppliersResult = Awaited<
  ReturnType<typeof searchPhotographerSuppliersAction>
>
type ListInput = Parameters<typeof getMediaImagesAction>[0]
type ListResult = Awaited<ReturnType<typeof getMediaImagesAction>>
type UpdateInput = Parameters<typeof updateMediaImageAction>[0]
type UpdateResult = Awaited<ReturnType<typeof updateMediaImageAction>>
type DeleteResult = Awaited<ReturnType<typeof deleteMediaImageAction>>
type GenerateFromUrlInput = Parameters<
  typeof generateImageMetadataFromUrlAction
>[0]
type GenerateFromUrlResult = Awaited<
  ReturnType<typeof generateImageMetadataFromUrlAction>
>

// HTTP client for the /api/media/* route handlers — used by Pages Router
// consumers that cannot import the Server Actions directly.
export class MediaApi extends BaseApi {
  constructor(fetcher: Fetcher) {
    super(fetcher)
  }

  async presign(input: PresignInput): ApiHandlerResult<PresignResult> {
    try {
      const res = await this.fetcher.post('/media/presign', {
        body: JSON.stringify(input),
        credentials: 'include',
      })
      return await this.parseBodyOrThrow(res)
    } catch (error: any) {
      return [error, null]
    }
  }

  async generateMetadata(
    input: GenerateInput
  ): ApiHandlerResult<GenerateResult> {
    try {
      const res = await this.fetcher.post('/media/generate-metadata', {
        body: JSON.stringify(input),
        credentials: 'include',
      })
      return await this.parseBodyOrThrow(res)
    } catch (error: any) {
      return [error, null]
    }
  }

  async createArticleImage(
    input: ArticleImageInput
  ): ApiHandlerResult<ArticleImageResult> {
    try {
      const res = await this.fetcher.post('/media/article-image', {
        body: JSON.stringify(input),
        credentials: 'include',
      })
      return await this.parseBodyOrThrow(res)
    } catch (error: any) {
      return [error, null]
    }
  }

  async searchSuppliers(q: string): ApiHandlerResult<SearchSuppliersResult> {
    try {
      const res = await this.fetcher.get(
        `/media/suppliers?q=${encodeURIComponent(q)}`,
        { credentials: 'include' }
      )
      return await this.parseBodyOrThrow(res)
    } catch (error: any) {
      return [error, null]
    }
  }

  async listAssets(input: ListInput): ApiHandlerResult<ListResult> {
    try {
      const res = await this.fetcher.post('/media/list', {
        body: JSON.stringify(input ?? {}),
        credentials: 'include',
      })
      return await this.parseBodyOrThrow(res)
    } catch (error: any) {
      return [error, null]
    }
  }

  async updateAsset(input: UpdateInput): ApiHandlerResult<UpdateResult> {
    try {
      const res = await this.fetcher.post('/media/update', {
        body: JSON.stringify(input),
        credentials: 'include',
      })
      return await this.parseBodyOrThrow(res)
    } catch (error: any) {
      return [error, null]
    }
  }

  async deleteAsset(id: number): ApiHandlerResult<DeleteResult> {
    try {
      const res = await this.fetcher.post('/media/delete', {
        body: JSON.stringify({ id }),
        credentials: 'include',
      })
      return await this.parseBodyOrThrow(res)
    } catch (error: any) {
      return [error, null]
    }
  }

  async generateMetadataFromUrl(
    input: GenerateFromUrlInput
  ): ApiHandlerResult<GenerateFromUrlResult> {
    try {
      const res = await this.fetcher.post('/media/generate-from-url', {
        body: JSON.stringify(input),
        credentials: 'include',
      })
      return await this.parseBodyOrThrow(res)
    } catch (error: any) {
      return [error, null]
    }
  }
}
